import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import {
  LogOut, Loader2, Mail, MessageCircle, Shield, ChevronRight,
  Trash2, BarChart3, FileText, RotateCcw, CreditCard, Ruler, Settings as SettingsIcon,
  Zap, TrendingUp, User as UserIcon, LifeBuoy, Copy, Check, Receipt, Globe, ShieldAlert, Megaphone,
  Flag, Star,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edge-error";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LANGUAGES } from "@/lib/languages";
import { useToast } from "@/hooks/use-toast";
// Theme picker removed — app is intentionally always-dark.
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useCredits } from "@/hooks/useCredits";
import { formatCredits } from "@/lib/format-credits";
import { cn } from "@/lib/utils";
import { StitchPage, StitchPanel, StitchHeading, Reveal } from "@/components/stitch";
import { MobileShell } from "@/components/mobile";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { CreditsUsageCard } from "@/components/CreditsUsageCard";
import { BroadcastBanner } from "@/components/BroadcastBanner";

import { useIsMobile } from "@/hooks/use-mobile";
import { useRegion } from "@/hooks/useRegion";
import { ProfileDesktop } from "./profile/ProfileDesktop";

import { setOnboardedCache } from "@/components/RequireAuth";
import { clearPaywallState } from "@/hooks/usePaywallTriggers";
import { SEO } from "@/components/SEO";
import { UserTierBadge } from "@/components/UserTierBadge";

const SUPPORT_EMAIL = "support@fitauraai.com";
const WHATSAPP_PHONE = "918328261165";

/** True on desktop browsers (rough UA sniff — good enough for picking link target). */
const isDesktopUA = () => {
  if (typeof window === "undefined") return false;
  return !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
};

/** wa.me — used as the mobile href. Desktop intercepts the click and shows a copy dialog. */
const waMeHref = (text: string) =>
  `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(text)}`;

/** mailto — used as the mobile href. Desktop intercepts the click and shows a copy dialog. */
const mailtoHref = (subject: string, body = "") => {
  const enc = encodeURIComponent;
  return `mailto:${SUPPORT_EMAIL}?subject=${enc(subject)}${body ? `&body=${enc(body)}` : ""}`;
};

const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const BODY_TYPES = ["Slim", "Athletic", "Average", "Curvy", "Plus"];
const SKIN_TONES = ["Fair", "Light", "Medium", "Tan", "Brown", "Deep"];
const STYLES = ["Casual", "Streetwear", "Formal", "Party", "Sporty", "Minimal", "Boho", "Vintage"];
const COLORS = ["Black", "White", "Neutral", "Pastel", "Bright", "Earth", "Jewel", "Monochrome"];

type Panel = null | "specs" | "settings" | "support" | "credits";

const Profile = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Theme picker removed (app is always-dark).
  const isAdmin = useIsAdmin();
  const { credits } = useCredits();
  const isMobile = useIsMobile();
  const region = useRegion();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [tryonCount, setTryonCount] = useState<number | null>(null);
  const [openPanel, setOpenPanel] = useState<Panel>(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [contactInfo, setContactInfo] = useState<{
    title: string;
    description: string;
    /** Primary copyable value — email address. */
    value: string;
    valueLabel: string;
    /** Optional pre-filled message body. */
    message?: string;
    /** WhatsApp QR link — shown instead of phone number on desktop. */
    whatsappQrLink?: string;
  } | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [skinTone, setSkinTone] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [size, setSize] = useState("");
  const [styles, setStyles] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);
  const [language, setLanguage] = useState<string>("en");

  // SEO handled by <SEO /> component in render.

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([
      supabase.from("profiles").select("*").eq("user_id", user.id).maybeSingle(),
      supabase.from("tryons").select("id", { count: "exact", head: true }).eq("user_id", user.id),
    ]).then(([{ data }, { count }]) => {
      if (cancelled) return;
      if (data) {
        setDisplayName(data.display_name ?? "");
        setGender(data.gender ?? "");
        setBodyType(data.body_type ?? "");
        setSkinTone(data.skin_tone ?? "");
        setHeightCm(data.height_cm ? String(data.height_cm) : "");
        setSize(data.size ?? "");
        setStyles(data.favorite_styles ?? []);
        setColors(data.color_preferences ?? []);
        setLanguage((data as { preferred_language?: string }).preferred_language ?? "en");
      }
      setTryonCount(count ?? 0);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user]);

  const toggle = (arr: string[], v: string, setter: (s: string[]) => void) =>
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const save = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        display_name: displayName.trim() || null,
        gender: gender || null,
        body_type: bodyType || null,
        skin_tone: skinTone || null,
        height_cm: heightCm ? Number(heightCm) : null,
        size: size || null,
        favorite_styles: styles,
        color_preferences: colors,
        preferred_language: language || "en",
      } as never)
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toast({ title: "Couldn't save", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Profile updated" });
  };

  const saveLanguage = async (next: string) => {
    setLanguage(next);
    if (!user) return;
    const { error } = await supabase
      .from("profiles")
      .update({ preferred_language: next } as never)
      .eq("user_id", user.id);
    if (error) {
      toast({ title: "Couldn't save language", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Language updated", description: "AI tips will use this language going forward." });
  };

  const deleteAccount = async () => {
    if (!user) return;
    setDeleting(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account", { body: {} });
      if (error) throw error;
      // Wipe client-side state tied to this uid so a fresh signup starts clean.
      clearPaywallState(user.id);
      setOnboardedCache(user.id, false);
      await supabase.auth.signOut();
      toast({ title: "Account deleted", description: "Your data has been permanently removed." });
      navigate("/auth", { replace: true });
    } catch (e) {
      const { message } = await readEdgeError(e);
      toast({
        title: "Couldn't delete account",
        description: message || "Please try again.",
        variant: "destructive",
      });
      setDeleting(false);
    }
  };

  const resetOnboarding = async () => {
    if (!user) return;
    const { error } = await supabase
      .from("profiles")
      .update({ onboarded: false })
      .eq("user_id", user.id);
    if (error) {
      toast({ title: "Couldn't reset", description: error.message, variant: "destructive" });
      return;
    }
    setOnboardedCache(user.id, false);
    toast({ title: "Onboarding reset", description: "Redirecting…" });
    navigate("/onboarding", { replace: true });
  };

  const Pill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1.5 text-sm font-medium transition-all",
        active
          ? "border-primary bg-primary text-primary-foreground shadow-[0_0_18px_-6px_hsl(var(--primary))]"
          : "border-white/15 bg-white/[0.03] text-foreground hover:border-primary/50 hover:bg-white/[0.06]",
      )}
    >
      {children}
    </button>
  );

  const initials = (displayName || user?.email || "?")
    .split(/\s+|@/)[0]
    .slice(0, 2)
    .toUpperCase();

  // Pull avatar from Google / OAuth provider metadata.
  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const avatarUrl =
    (typeof meta.avatar_url === "string" && meta.avatar_url) ||
    (typeof meta.picture === "string" && meta.picture) ||
    "";

  if (loading) {
    return (
      <StitchPage glow={false}>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </StitchPage>
    );
  }

  // Desktop redesign — Stitch "Personal Identity" reference.
  if (!isMobile) {
    return (
      <>
        <SEO noindex />
        <ProfileDesktop
          // Identity
          displayName={displayName}
          email={user?.email ?? ""}
          avatarUrl={avatarUrl}
          initials={initials}
          credits={credits}
          tryonCount={tryonCount}
          // Preferences
          region={region}
          // Auth & navigation
          onSignOut={async () => { await signOut(); navigate("/auth"); }}
          onBuyCredits={() => setBuyOpen(true)}
          onViewPurchases={() => navigate("/app/purchases")}
          // Body specs
          setDisplayName={setDisplayName}
          gender={gender} setGender={setGender}
          bodyType={bodyType} setBodyType={setBodyType}
          skinTone={skinTone} setSkinTone={setSkinTone}
          heightCm={heightCm} setHeightCm={setHeightCm}
          size={size} setSize={setSize}
          styles={styles} setStyles={setStyles}
          colors={colors} setColors={setColors}
          onSaveSpecs={() => void save()}
          saving={saving}
          language={language}
          onLanguageChange={(next) => void saveLanguage(next)}
          // Support handlers — open the same ContactCopyDialog used by mobile
          onContactEmail={() =>
            setContactInfo({
              title: "Email support",
              description: "Email isn't set up on this device. Copy our address and send from your inbox.",
              value: SUPPORT_EMAIL,
              valueLabel: "Support email",
              message: "FitAura AI support request",
            })
          }
          onContactWhatsApp={() =>
            setContactInfo({
              title: "WhatsApp support",
              description: "Scan the QR code with your mobile camera to open WhatsApp chat for support.",
              value: `+${WHATSAPP_PHONE}`,
              valueLabel: "WhatsApp number",
              message: "Hi FitAura AI team, I need support with:",
              whatsappQrLink: "https://wa.me/message/SOUKJ4YQCE5SC1",
            })
          }
          onContactFeedback={() =>
            setContactInfo({
              title: "Send feedback on WhatsApp",
              description: "Scan the QR code with your mobile camera to open WhatsApp and send your feedback.",
              value: `+${WHATSAPP_PHONE}`,
              valueLabel: "WhatsApp number",
              message: "Hi FitAura AI team, I'd like to share some feedback:",
              whatsappQrLink: "https://wa.me/message/SOUKJ4YQCE5SC1",
            })
          }
          // Admin & dev
          isAdmin={isAdmin}
          onResetOnboarding={() => void resetOnboarding()}
          // Danger zone
          onDelete={() => void deleteAccount()}
          deleting={deleting}
          confirmText={confirmText}
          setConfirmText={setConfirmText}
        />
        <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />
        <ContactCopyDialog info={contactInfo} onClose={() => setContactInfo(null)} />
      </>
    );
  }

  return (
    <>
      <SEO noindex />
    <StitchPage glow={false}>
      <MobileShell title="Profile">
      <div className="mx-auto w-full max-w-md pt-4 pb-10">
        {/* Header — avatar + name */}
        <Reveal y={12} duration={0.45}>
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-primary/60 bg-gradient-to-br from-primary/30 to-accent/20 font-display text-xl font-bold text-foreground shadow-[0_0_24px_-4px_hsl(var(--primary))]">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={displayName || user?.email || "Profile"}
                    referrerPolicy="no-referrer"
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : initials ? (
                  initials
                ) : (
                  <UserIcon className="h-7 w-7" />
                )}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-background bg-primary shadow-[0_0_10px_hsl(var(--primary))]" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-display truncate text-2xl font-bold uppercase italic tracking-tight text-foreground">
                {displayName || "Your Aura"}
              </h1>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              <div className="mt-2">
                <UserTierBadge size="sm" />
              </div>
            </div>
            <button
              type="button"
              onClick={async () => { await signOut(); navigate("/auth"); }}
              className="rounded-full border border-white/10 bg-white/[0.03] p-2 text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </Reveal>

        {/* Announcements */}
        <Reveal y={12} duration={0.45} delay={0.03}>
          <div className="mt-6">
            <BroadcastBanner />
          </div>
        </Reveal>

        {/* Stat cards */}
        <Reveal y={12} duration={0.45} delay={0.05}>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <StitchPanel className="group/stat relative overflow-hidden p-4 transition-all hover:-translate-y-0.5 hover:shadow-elevated" brackets={false}>
              {/* Glossy gradient backdrop */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5"
              />
              {/* Continuous shimmer */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,transparent_30%,hsl(var(--primary)/0.07)_50%,transparent_70%)] bg-[length:200%_100%] animate-shimmer"
              />
              {/* Hover sheen sweep */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-primary-foreground/10 to-transparent transition-transform duration-1000 ease-out group-hover/stat:translate-x-full"
              />
              <div className="relative">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary shadow-soft ring-1 ring-primary/20">
                  <Zap className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <p className="font-display mt-3 text-3xl font-bold tabular-nums text-foreground">{formatCredits(credits)}</p>
                <p className="font-display mt-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  Credits Left
                </p>
              </div>
            </StitchPanel>
            <StitchPanel className="group/stat relative overflow-hidden p-4 transition-all hover:-translate-y-0.5 hover:shadow-elevated" brackets={false}>
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-primary/5"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,transparent_30%,hsl(var(--accent)/0.07)_50%,transparent_70%)] bg-[length:200%_100%] animate-shimmer"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-primary-foreground/10 to-transparent transition-transform duration-1000 ease-out group-hover/stat:translate-x-full"
              />
              <div className="relative">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/15 text-accent shadow-soft ring-1 ring-accent/20">
                  <TrendingUp className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <p className="font-display mt-3 text-3xl font-bold tabular-nums text-foreground">{tryonCount ?? 0}</p>
                <p className="font-display mt-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  Try-Ons Made
                </p>
              </div>
            </StitchPanel>
          </div>
        </Reveal>

        {/* Credits usage breakdown */}
        <Reveal y={12} duration={0.45} delay={0.07}>
          <div className="mt-4">
            <CreditsUsageCard onBuyCredits={() => setBuyOpen(true)} />
          </div>
        </Reveal>

        {/* Action rows */}
        <Reveal y={12} duration={0.45} delay={0.1}>
          <div className="mt-4 space-y-2.5">
            <ActionRow
              icon={<CreditCard className="h-5 w-5" />}
              label="Buy Credits"
              onClick={() => setBuyOpen(true)}
            />
            <ActionRow
              icon={<Receipt className="h-5 w-5" />}
              label="Purchase History"
              onClick={() => navigate("/app/purchases")}
            />
            <ActionRow
              icon={<Ruler className="h-5 w-5" />}
              label="Body Measurement Specs"
              onClick={() => setOpenPanel(openPanel === "specs" ? null : "specs")}
              expanded={openPanel === "specs"}
            />
            {openPanel === "specs" && (
              <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                <StitchPanel className="space-y-4 p-5" brackets={false}>
                  <div className="space-y-1.5">
                    <Label htmlFor="profile-display-name">Display name</Label>
                    <Input
                      id="profile-display-name"
                      name="displayName"
                      autoComplete="name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      maxLength={60}
                    />
                  </div>
                  <div className="space-y-2">
                    <span className="block text-sm font-medium leading-none">Gender</span>
                    <div className="flex flex-wrap gap-2">
                      {GENDERS.map((g) => <Pill key={g} active={gender === g} onClick={() => setGender(g)}>{g}</Pill>)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="block text-sm font-medium leading-none">Body type</span>
                    <div className="flex flex-wrap gap-2">
                      {BODY_TYPES.map((b) => <Pill key={b} active={bodyType === b} onClick={() => setBodyType(b)}>{b}</Pill>)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="block text-sm font-medium leading-none">Skin tone</span>
                    <div className="flex flex-wrap gap-2">
                      {SKIN_TONES.map((s) => <Pill key={s} active={skinTone === s} onClick={() => setSkinTone(s)}>{s}</Pill>)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="profile-height-cm">Height (cm)</Label>
                      <Input
                        id="profile-height-cm"
                        name="heightCm"
                        autoComplete="off"
                        type="number"
                        inputMode="numeric"
                        value={heightCm}
                        onChange={(e) => setHeightCm(e.target.value)}
                        min={100}
                        max={230}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="profile-size">Size</Label>
                      <Input
                        id="profile-size"
                        name="size"
                        autoComplete="off"
                        value={size}
                        onChange={(e) => setSize(e.target.value)}
                        maxLength={6}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="block text-sm font-medium leading-none">Favorite styles</span>
                    <div className="flex flex-wrap gap-2">
                      {STYLES.map((s) => (
                        <Pill key={s} active={styles.includes(s)} onClick={() => toggle(styles, s, setStyles)}>{s}</Pill>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="block text-sm font-medium leading-none">Color preferences</span>
                    <div className="flex flex-wrap gap-2">
                      {COLORS.map((c) => (
                        <Pill key={c} active={colors.includes(c)} onClick={() => toggle(colors, c, setColors)}>{c}</Pill>
                      ))}
                    </div>
                  </div>
                  <Button
                    onClick={save}
                    disabled={saving}
                    className="font-display w-full bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-[0_0_24px_-6px_hsl(var(--primary))] hover:opacity-95"
                  >
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Specs
                  </Button>
                </StitchPanel>
              </div>
            )}

            <ActionRow
              icon={<SettingsIcon className="h-5 w-5" />}
              label="Settings"
              onClick={() => setOpenPanel(openPanel === "settings" ? null : "settings")}
              expanded={openPanel === "settings"}
            />
            {openPanel === "settings" && (
              <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                <div className="space-y-3">

                  {/* Appearance section removed — app is intentionally always-dark. */}

                  {/* Dev tools */}
                  {import.meta.env.DEV && (
                    <StitchPanel className="space-y-3 border-primary/30 p-5" brackets={false}>
                      <div>
                        <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-primary">
                          Dev Tools
                        </h2>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Re-run the onboarding flow for this account.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full border-primary/40 text-primary hover:bg-primary/10"
                        onClick={() => void resetOnboarding()}
                      >
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Reset onboarding
                      </Button>
                    </StitchPanel>
                  )}

                  {/* Danger zone */}
                  <StitchPanel className="space-y-3 border-destructive/30 p-5" brackets={false}>
                    <div>
                      <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-destructive">
                        Danger Zone
                      </h2>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Permanently delete your account, photos, try-ons, and all related data. Cannot be undone.
                      </p>
                    </div>

                    <AlertDialog onOpenChange={(o) => !o && setConfirmText("")}>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="w-full" disabled={deleting}>
                          {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                          Delete my account & data
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete your FitAura AI account?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently removes your profile, all uploaded photos, try-on history, and remaining credits. This action is irreversible.
                            <br /><br />
                            Type <span className="font-mono font-bold text-foreground">DELETE</span> below to confirm.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <Input
                          id="profile-delete-confirm"
                          name="deleteConfirm"
                          autoComplete="off"
                          value={confirmText}
                          onChange={(e) => setConfirmText(e.target.value)}
                          placeholder="DELETE"
                          autoFocus
                        />
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); void deleteAccount(); }}
                            disabled={deleting || confirmText !== "DELETE"}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Permanently delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </StitchPanel>
                </div>
              </div>
            )}

            <ActionRow
              icon={<LifeBuoy className="h-5 w-5" />}
              label="Support"
              onClick={() => setOpenPanel(openPanel === "support" ? null : "support")}
              expanded={openPanel === "support"}
            />
            {openPanel === "support" && (
              <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                <StitchPanel className="space-y-2 p-5" brackets={false}>
                  <StitchHeading
                    eyebrow="SUPPORT"
                    title="Help & Resources"
                    subtitle="We typically reply within 1–2 business days."
                  />
                  <LinkRow
                    href={mailtoHref("FitAura AI support request")}
                    desktopAction={() =>
                      setContactInfo({
                        title: "Email support",
                        description: "Email isn't set up on this device. Copy our address and send from your inbox.",
                        value: SUPPORT_EMAIL,
                        valueLabel: "Support email",
                        message: "FitAura AI support request",
                      })
                    }
                    icon={<Mail className="h-4 w-4" />}
                    title="Email support"
                    subtitle={SUPPORT_EMAIL}
                  />
                  <LinkRow
                    href={waMeHref("Hi FitAura AI team, I'd like to share some feedback:")}
                    desktopAction={() =>
                      setContactInfo({
                        title: "Send feedback on WhatsApp",
                        description: "Scan the QR code with your mobile camera to open WhatsApp and send your feedback.",
                        value: `+${WHATSAPP_PHONE}`,
                        valueLabel: "WhatsApp number",
                        message: "Hi FitAura AI team, I'd like to share some feedback:",
                        whatsappQrLink: "https://wa.me/message/SOUKJ4YQCE5SC1",
                      })
                    }
                    icon={<MessageCircle className="h-4 w-4" />}
                    title="Send feedback"
                    subtitle="Ideas to make FitAura AI better (WhatsApp)"
                  />
                  <LinkRow
                    href={waMeHref("Hi FitAura AI team, I need support with:")}
                    desktopAction={() =>
                      setContactInfo({
                        title: "WhatsApp support",
                        description: "Scan the QR code with your mobile camera to open WhatsApp chat for support.",
                        value: `+${WHATSAPP_PHONE}`,
                        valueLabel: "WhatsApp number",
                        message: "Hi FitAura AI team, I need support with:",
                        whatsappQrLink: "https://wa.me/message/SOUKJ4YQCE5SC1",
                      })
                    }
                    icon={<MessageCircle className="h-4 w-4" />}
                    title="WhatsApp Support"
                    subtitle="Chat with us on WhatsApp"
                  />
                  <LinkRow
                    href={waMeHref("Hi FitAura AI team, I'd like to REPORT offensive or inappropriate AI-generated content. Details:\n\n- What I saw:\n- Where (screen / try-on ID):\n- Date/time:")}
                    desktopAction={() =>
                      setContactInfo({
                        title: "Report content on WhatsApp",
                        description: "Scan the QR code with your mobile camera to open WhatsApp and report offensive AI-generated content.",
                        value: `+${WHATSAPP_PHONE}`,
                        valueLabel: "WhatsApp number",
                        message: "Hi FitAura AI team, I'd like to REPORT offensive or inappropriate AI-generated content. Details:",
                        whatsappQrLink: "https://wa.me/message/SOUKJ4YQCE5SC1",
                      })
                    }
                    icon={<Flag className="h-4 w-4" />}
                    title="Report content"
                    subtitle="Flag offensive AI-generated content (WhatsApp)"
                  />
                  <LinkRow
                    href="https://play.google.com/store/apps/details?id=com.fitauraai.app"
                    desktopAction={() =>
                      setContactInfo({
                        title: "Rate FitAura AI on Google Play",
                        description: "Open the Google Play listing on your phone to leave a rating.",
                        value: "https://play.google.com/store/apps/details?id=com.fitauraai.app",
                        valueLabel: "Play Store link",
                      })
                    }
                    icon={<Star className="h-4 w-4" />}
                    title="Rate us on Play Store"
                    subtitle="Love the app? Leave a 5-star rating ⭐"
                  />
                  <LinkRow to="/privacy" icon={<Shield className="h-4 w-4" />} title="Privacy policy" subtitle="How we handle your photos & data" />
                  <LinkRow to="/terms" icon={<FileText className="h-4 w-4" />} title="Terms of Service" subtitle="Account use, AI content & billing" />
                  <LinkRow to="/refund" icon={<RotateCcw className="h-4 w-4" />} title="Refund policy" subtitle="30-day money-back guarantee" />
                  {isAdmin && (
                    <LinkRow to="/admin" icon={<BarChart3 className="h-4 w-4" />} title="Admin console" subtitle="Revenue, users, ops, mobile" />
                  )}
                </StitchPanel>
              </div>
            )}
          </div>
        </Reveal>

        <p className="font-display mt-8 text-center text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70">
          FitAura AI · v1.0.0
        </p>
      </div>
      </MobileShell>

      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />
      <ContactCopyDialog info={contactInfo} onClose={() => setContactInfo(null)} />
    </StitchPage>
    </>
  );
};

/* ---------- Sub-components ---------- */

const ActionRow = ({
  icon,
  label,
  onClick,
  tone = "default",
  expanded = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: "default" | "primary";
  expanded?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "group flex w-full items-center justify-between rounded-2xl border bg-white/[0.03] px-4 py-3.5 backdrop-blur-xl transition-all",
      tone === "primary"
        ? "border-primary/40 hover:border-primary hover:bg-primary/5 hover:shadow-[0_0_24px_-8px_hsl(var(--primary))]"
        : "border-white/10 hover:border-primary/40 hover:bg-white/[0.06]",
      expanded && "border-primary/40 bg-white/[0.06]",
    )}
  >
    <div className="flex items-center gap-3">
      <span
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-xl",
          tone === "primary" ? "bg-primary/15 text-primary" : "bg-white/[0.05] text-foreground",
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          "text-sm font-semibold",
          tone === "primary" ? "text-primary" : "text-foreground",
        )}
      >
        {label}
      </span>
    </div>
    <ChevronRight
      className={cn(
        "h-4 w-4 text-muted-foreground transition-transform",
        expanded && "rotate-90 text-primary",
      )}
    />
  </button>
);

const LinkRow = ({
  href,
  to,
  icon,
  title,
  subtitle,
  desktopAction,
}: {
  href?: string;
  to?: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  /** When provided, desktop clicks call this instead of opening `href`. */
  desktopAction?: () => void;
}) => {
  const className =
    "flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] p-3 text-left transition-colors hover:border-primary/40 hover:bg-white/[0.05]";
  const inner = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="whitespace-normal break-words text-xs leading-snug text-muted-foreground">
            {subtitle}
          </p>
        </div>
      </div>
      <ChevronRight className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
    </>
  );

  if (href) {
    // On desktop, prefer the in-app copy dialog over launching mailto/wa.me.
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (desktopAction && isDesktopUA()) {
        e.preventDefault();
        desktopAction();
      }
    };
    return (
      <a
        href={href}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {inner}
      </a>
    );
  }
  return <Link to={to!} className={className}>{inner}</Link>;
};

/* ---------- Desktop "copy contact info" dialog ---------- */

interface ContactInfo {
  title: string;
  description: string;
  value: string;
  valueLabel: string;
  message?: string;
  /** WhatsApp QR code link — shown on desktop for WhatsApp options. */
  whatsappQrLink?: string;
}

const ContactCopyDialog = ({
  info,
  onClose,
}: {
  info: ContactInfo | null;
  onClose: () => void;
}) => {
  const { toast } = useToast();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (info?.whatsappQrLink) {
      QRCode.toDataURL(info.whatsappQrLink, { width: 200, margin: 2 })
        .then((url) => setQrDataUrl(url))
        .catch(() => setQrDataUrl(null));
    } else {
      setQrDataUrl(null);
    }
  }, [info?.whatsappQrLink]);

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast({ title: "Copied to clipboard" });
      setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1600);
    } catch {
      toast({ title: "Copy failed", description: "Please copy manually.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={!!info} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{info?.title}</DialogTitle>
          <DialogDescription>{info?.description}</DialogDescription>
        </DialogHeader>

        {info && (
          <div className="mt-2 space-y-4">
            {info.whatsappQrLink && qrDataUrl && (
              <div className="flex flex-col items-center rounded-lg border border-primary/30 bg-primary/5 p-4">
                <img
                  src={qrDataUrl}
                  alt="WhatsApp QR Code"
                  className="h-40 w-40 rounded-lg bg-white p-2"
                />
                <p className="mt-3 text-center text-xs text-muted-foreground">
                  Scan with your mobile camera to open WhatsApp
                </p>
              </div>
            )}
            {!info.whatsappQrLink && (
              <CopyField
                label={info.valueLabel}
                value={info.value}
                copied={copiedKey === "value"}
                onCopy={() => copy("value", info.value)}
              />
            )}
            {info.message && (
              <CopyField
                label="Message"
                value={info.message}
                multiline
                copied={copiedKey === "message"}
                onCopy={() => copy("message", info.message!)}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CopyField = ({
  label, value, copied, onCopy, multiline = false,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  multiline?: boolean;
}) => (
  <div className="rounded-xl border border-border bg-muted/30 p-3">
    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </p>
    <div className="flex items-start gap-2">
      <p className={cn(
        "flex-1 text-sm text-foreground",
        multiline ? "whitespace-pre-wrap break-words" : "break-all font-medium",
      )}>
        {value}
      </p>
      <Button
        size="sm"
        variant={copied ? "secondary" : "default"}
        onClick={onCopy}
        className="shrink-0"
      >
        {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  </div>
);

export default Profile;
